import { useReducer, useEffect, useRef, useCallback } from 'preact/hooks';
import { camelizeKeys } from 'humps';
import { buildCalendarApiUrl } from '../utils/calendarServiceHelpers';

/**
 * Custom hook to fetch calendar events from the API
 * 
 * Features:
 * - Generic: works for any event type
 * - Efficient: only fetches when origin and destination are available
 * - Resilient: handles errors without breaking UI
 * - Cancelable: ignores stale requests when dependencies change
 * 
 * @param {Object} params
 * @param {string} params.origin - Origin slug (e.g., 'cdmx')
 * @param {string} params.destination - Destination slug (e.g., 'monterrey')
 * @param {string|null} params.startDate - Start date for events range in YYYY-MM-DD format (optional)
 * @param {string|null} params.endDate - End date for events range in YYYY-MM-DD format (optional)
 * @param {boolean} params.enabled - Whether to enable fetching (optional, default: true)
 * @param {Object} params.options - Configuration options
 * @param {string} params.options.sourceUrl - Source URL from config (e.g., 'www.reservamos.mx/api/v2')
 * @param {boolean} params.options.forceHttps - Force HTTPS protocol (optional, default: false)
 * @returns {Object} { events, fetching, fetched, error, refetch }
 */
const useCalendarEvents = ({
  origin,
  destination,
  startDate,
  endDate,
  enabled = true,
  options = {},
}) => {
  const {
    sourceUrl,
    forceHttps = false,
  } = options;

  // State management with useReducer
  const initialState = {
    events: [],
    fetching: false,
    fetched: false,
    error: null,
  };

  const eventsReducer = (state, action) => {
    switch (action.type) {
      case 'FETCH_START':
        return {
          ...state,
          fetching: true,
          error: null,
        };
      case 'FETCH_SUCCESS':
        return {
          ...state,
          events: action.payload,
          fetching: false,
          fetched: true,
          error: null,
        };
      case 'FETCH_ERROR':
        return {
          ...state,
          fetching: false,
          fetched: false,
          error: action.payload,
        };
      case 'FETCH_CANCEL':
        return {
          ...state,
          fetching: false,
          fetched: false,
        };
      case 'RESET':
        return {
          ...initialState,
        };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(eventsReducer, initialState);
  const { events, fetching, fetched, error } = state;

  // Refs for cancellation and tracking
  const abortControllerRef = useRef(null);
  const lastEffectParamsRef = useRef(null);

  /**
   * Fetch calendar events from the API
   * Implements graceful degradation: logs errors but doesn't throw
   * 
   * @param {Object} params
   * @param {AbortSignal} params.signal - AbortSignal to cancel the request
   * @param {string} params.origin - Origin slug
   * @param {string} params.destination - Destination slug
   * @param {string} params.sourceUrl - Source URL
   * @param {string} params.startDate - Start date in YYYY-MM-DD format
   * @param {string} params.endDate - End date in YYYY-MM-DD format
   * @returns {Promise<Array>} Array of events (empty array on error)
   */
  const fetchCalendarEvents = useCallback(async ({ signal, origin: fetchOrigin, destination: fetchDestination, sourceUrl: fetchSourceUrl, startDate: fetchStartDate, endDate: fetchEndDate }) => {
    try {
      const apiUrl = buildCalendarApiUrl({
        origin: fetchOrigin,
        destination: fetchDestination,
        sourceUrl: fetchSourceUrl,
        forceHttps,
        startDate: fetchStartDate,
        endDate: fetchEndDate,
      });

      if (!apiUrl) {
        console.error('[useCalendarEvents] Invalid API URL parameters', {
          origin: fetchOrigin,
          destination: fetchDestination,
          sourceUrl: fetchSourceUrl,
        });
        return [];
      }

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        signal,
      });

      // Check if request was aborted
      if (signal.aborted) {
        return [];
      }

      if (!response.ok) {
        console.error('[useCalendarEvents] HTTP error', {
          status: response.status,
          statusText: response.statusText,
          url: apiUrl,
        });
        return [];
      }

      const data = await response.json();
      const camelizedData = camelizeKeys(data);

      // Handle response (API returns { calendar: [...] } or array directly)
      const eventsData = Array.isArray(camelizedData) ? camelizedData : (camelizedData.calendar || []);
      
      return eventsData;
    } catch (err) {
      // Graceful degradation: log error but don't throw
      // Silently return empty array to prevent breaking the UI
      console.error('[useCalendarEvents] Error fetching calendar events', {
        error: err,
        message: err.message,
        origin: fetchOrigin,
        destination: fetchDestination,
        sourceUrl: fetchSourceUrl,
      });
      return [];
    }
  }, [forceHttps]);

  /**
   * Fetch events from the API
   * Note: Validation and abort logic is handled by the useEffect caller
   * @param {Object} params - Parameters for the fetch
   * @param {string} params.origin - Origin slug
   * @param {string} params.destination - Destination slug
   * @param {string} params.sourceUrl - Source URL
   * @param {string} params.startDate - Start date in YYYY-MM-DD format
   * @param {string} params.endDate - End date in YYYY-MM-DD format
   */
  const fetchEvents = useCallback(async ({ origin: fetchOrigin, destination: fetchDestination, sourceUrl: fetchSourceUrl, startDate: fetchStartDate, endDate: fetchEndDate }) => {
    // Create new AbortController for this request
    // Note: Previous abort is handled by useEffect
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    dispatch({ type: 'FETCH_START' });

    try {
      const eventsData = await fetchCalendarEvents({ 
        signal: abortController.signal,
        origin: fetchOrigin,
        destination: fetchDestination,
        sourceUrl: fetchSourceUrl,
        startDate: fetchStartDate,
        endDate: fetchEndDate,
      });
      
      // Check if request was aborted before updating state
      if (abortController.signal.aborted) {
        return;
      }
      
      dispatch({ 
        type: 'FETCH_SUCCESS', 
        payload: eventsData 
      });
    } catch (err) {
      // Don't set error if request was aborted (this is expected)
      if (err.name === 'AbortError' || abortController.signal.aborted) {
        return;
      }
      
      // Graceful degradation: log error but update state with empty array
      console.error('[useCalendarEvents] Unexpected error in fetchEvents', {
        error: err,
        message: err.message,
      });
      
      // Update state with empty array on error (graceful degradation)
      dispatch({ 
        type: 'FETCH_SUCCESS', 
        payload: [] 
      });
    }
  }, [fetchCalendarEvents]);

  /**
   * Manual refetch function
   */
  const refetch = useCallback(() => {
    dispatch({ type: 'RESET' });
    // Use current values directly (they're in the hook's closure)
    fetchEvents({ origin, destination, sourceUrl, startDate, endDate });
  }, [fetchEvents, origin, destination, sourceUrl, startDate, endDate]);

  // Effect to fetch events when dependencies change
  // Only fetch if the calculated request parameters have changed
  useEffect(() => {
    // Skip if missing required params or dates
    if (!enabled || !origin || !destination || !sourceUrl || !startDate || !endDate) {
      return;
    }

    // Create effect params identifier (includes all dependencies)
    const currentEffectParams = `${enabled}-${origin}-${destination}-${sourceUrl}-${startDate}-${endDate}`;
    
    // Skip if effect params are exactly the same (no need to fetch again)
    if (lastEffectParamsRef.current === currentEffectParams) {
      return;
    }
    
    // Abort previous request if it exists (before starting a new one)
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Update last effect params BEFORE starting fetch
    lastEffectParamsRef.current = currentEffectParams;

    // Use fetchEvents function which handles the actual fetch
    fetchEvents({ origin, destination, sourceUrl, startDate, endDate });

    // Cleanup: abort any pending requests when dependencies change or component unmounts
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [startDate, endDate, origin, destination, sourceUrl, enabled, fetchEvents]);

  return {
    events,
    fetching,
    fetched,
    error,
    refetch,
  };
};

export default useCalendarEvents;

